export const WRITING_ASSIST_V2_FLAG = 'aiWritingAssistV2';

/**
 * The API-backed startup bootstrap owns runtime feature-flag delivery. Only
 * an explicit boolean true enables v2; missing, malformed, string, timeout,
 * or request-error values fail closed to the unchanged legacy experience.
 */
export function isWritingAssistV2Enabled(source: unknown = (window as any)?.TempStars): boolean {
  if (!source || typeof source !== 'object') return false;
  const featureFlags = (source as any).FeatureFlags;
  return Boolean(
    featureFlags
      && typeof featureFlags === 'object'
      && featureFlags[WRITING_ASSIST_V2_FLAG] === true,
  );
}

type ClientWritingAssistFlags = {
  aiWritingAssistV2?: unknown;
  ce_courses_directory?: unknown;
};

type WindowWithTempStars = {
  TempStars?: unknown;
};

type ClientFlagLoader = (signal: AbortSignal) => Promise<ClientWritingAssistFlags>;

/**
 * Hydrate the global before React mounts so every call site sees one stable
 * decision for the lifetime of the fresh session. Delivery failures are OFF.
 */
export async function hydrateWritingAssistV2Flag(
  loadFlags: () => Promise<ClientWritingAssistFlags>,
  target: WindowWithTempStars = window as any,
): Promise<boolean> {
  let enabled = false;
  let loadedFlags: ClientWritingAssistFlags = {};
  try {
    loadedFlags = await loadFlags();
    enabled = loadedFlags?.[WRITING_ASSIST_V2_FLAG] === true;
  } catch (_error) {
    enabled = false;
  }

  if (!target.TempStars || typeof target.TempStars !== 'object' || Array.isArray(target.TempStars)) {
    target.TempStars = {};
  }
  const tempStars = target.TempStars as Record<string, unknown>;
  if (!tempStars.FeatureFlags
      || typeof tempStars.FeatureFlags !== 'object'
      || Array.isArray(tempStars.FeatureFlags)) {
    tempStars.FeatureFlags = {};
  }
  (tempStars.FeatureFlags as Record<string, unknown>)[WRITING_ASSIST_V2_FLAG] = enabled;
  // T2-6820: preserve the exact CE flag only when the public endpoint starts
  // returning a boolean. Missing/malformed values remain absent so the shared
  // directory runtime can use its single authenticated capability probe.
  const ceDirectoryFlag = loadedFlags?.ce_courses_directory;
  if (typeof ceDirectoryFlag === 'boolean') {
    (tempStars.FeatureFlags as Record<string, unknown>).ce_courses_directory = ceDirectoryFlag;
  }
  return enabled;
}

/**
 * Resolve the startup decision within a hard deadline. A request that ignores
 * abort may finish later, but it can no longer mutate the session decision.
 */
export async function bootstrapWritingAssistV2Flag(
  loadFlags: ClientFlagLoader,
  timeoutMs: number,
  target: WindowWithTempStars = window as any,
): Promise<boolean> {
  const controller = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;

  const request = Promise.resolve()
    .then(() => loadFlags(controller.signal))
    .then(flags => ({ flags }))
    .catch(() => ({ flags: {} }));
  const deadline = new Promise<{ flags: ClientWritingAssistFlags }>(resolve => {
    timeout = setTimeout(() => {
      controller.abort();
      resolve({ flags: {} });
    }, timeoutMs);
  });

  const outcome = await Promise.race([request, deadline]);
  if (timeout !== undefined) clearTimeout(timeout);
  return hydrateWritingAssistV2Flag(async () => outcome.flags, target);
}
